import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useStudentDepletionDetail } from "@/hooks/useStudentDepletionDetail";
import { format, parseISO } from "date-fns";
import { th } from "date-fns/locale";

interface Props {
  studentCode: string | null;
  studentName?: string | null;
  onClose: () => void;
}

function LearningStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    present: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
    absent: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    leave: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  };
  return (
    <Badge className={`border-0 ${map[status] ?? "bg-muted text-muted-foreground"}`}>
      {status}
    </Badge>
  );
}

function ClassScheduleTab({ studentCode }: { studentCode: string }) {
  const { classTemplates, isLoading, error } = useStudentDepletionDetail(studentCode);

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-destructive text-sm">{error}</div>;
  }

  if (classTemplates.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        ไม่มีตารางประจำ
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>วัน</TableHead>
          <TableHead>เวลา</TableHead>
          <TableHead>วิชา</TableHead>
          <TableHead>สถานที่</TableHead>
          <TableHead>ครู</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {classTemplates.map((t) => (
          <TableRow key={t.id}>
            <TableCell className="font-medium">{t.weekday_name}</TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {t.start_time && t.end_time
                ? `${t.start_time.slice(0, 5)}–${t.end_time.slice(0, 5)}`
                : t.start_time?.slice(0, 5) ?? "—"}
            </TableCell>
            <TableCell>{t.subject ?? "—"}</TableCell>
            <TableCell>{t.location ?? "—"}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{t.teacher_code ?? "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function LearningHistoryTab({ studentCode }: { studentCode: string }) {
  const { learningReports, isLoading, error } = useStudentDepletionDetail(studentCode);

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-destructive text-sm">{error}</div>;
  }

  if (learningReports.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        ไม่มีประวัติการเรียน
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>วันที่</TableHead>
          <TableHead>วิชา</TableHead>
          <TableHead>ครู</TableHead>
          <TableHead>ประเภท</TableHead>
          <TableHead>สถานที่</TableHead>
          <TableHead>สถานะ</TableHead>
          <TableHead>ระยะเวลา</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {learningReports.map((r, i) => (
          <TableRow key={i}>
            <TableCell className="text-sm">
              {r.teaching_date
                ? format(parseISO(r.teaching_date), "d MMM yy", { locale: th })
                : "—"}
            </TableCell>
            <TableCell>{r.subject}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{r.teacher_id}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{r.teaching_type ?? "—"}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{r.teaching_location}</TableCell>
            <TableCell>
              <LearningStatusBadge status={r.learning_status} />
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">{r.teaching_duration}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function StudentDepletionDetailModal({ studentCode, studentName, onClose }: Props) {
  const { classTemplates, learningReports } = useStudentDepletionDetail(studentCode);

  return (
    <Dialog open={!!studentCode} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {studentName ? `${studentName} (${studentCode})` : studentCode}
          </DialogTitle>
        </DialogHeader>

        {studentCode && (
          <Tabs defaultValue="schedule">
            <TabsList>
              <TabsTrigger value="schedule">
                ตารางประจำ {classTemplates.length > 0 && `(${classTemplates.length})`}
              </TabsTrigger>
              <TabsTrigger value="history">
                ประวัติการเรียน {learningReports.length > 0 && `(${learningReports.length})`}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="schedule" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <ClassScheduleTab studentCode={studentCode} />
              </div>
            </TabsContent>
            <TabsContent value="history" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <LearningHistoryTab studentCode={studentCode} />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
